// ═══════════════════════════════════════════════════════════════════════════
// targetExpiry.js — when a working target STOPS being workable, plus the sort
// and filter the Targets page is driven by.
//
// Kevin, 2026-08-24: "Note targets that have expired. Be able to sort/filter by
// date entered, contract board month, price, everything."
//
// ── THE EXPIRY RULE IS THE BOARD ROLL, AND IT IS BORROWED, NOT INVENTED ────
//
// An elevator rolls off a futures board the month BEFORE delivery, so a target
// resting on a contract whose delivery month has arrived can no longer be
// worked. That rule is already established in this repo — the paused grain
// target alerts skip `contract_date < first-of-this-month` for exactly this
// reason (see project_grain_target_alerts) — so this file reuses it rather
// than composing a second, differently-shaped answer to the same question.
//
// ── THE WINDOW IS ONLY ASSERTED WHEN THE WINDOW STATES ITS YEAR ───────────
//
// A shipment window that has already passed is also dead, but "Oct-Nov" and
// "HARVEST" carry NO year. The year could be inferred from the contract —
// month <= delivery month means the same year — and that inference is right
// for `Oct-Nov` against Z26 and WRONG for `HARVEST` against U26, where the
// elevator rolls to the September board for a JULY delivery (CLAUDE.md records
// exactly that: "harvest = July-Aug delivery, but the elevator rolls to the Sep
// board at end of June").
//
// So a window is only called passed when it SAYS its year — `26-Dec`, `27-Sep`,
// `2027 HARV`. Otherwise the window is reported as `window_year_not_stated` and
// decides nothing, and the board roll — which is always computable from the
// contract symbol — carries the answer alone. An expiry we cannot read must not
// become an expiry we assumed: marking a live target dead takes it off the list
// Kevin works from, which is the same silent-shortening failure the partition
// invariant in check-cash-targets exists to prevent.
//
// PURE — no fetch, no React, no Supabase, and NO `new Date()` without an
// argument: `asOf` is always passed in so this is testable and reproducible.
// ═══════════════════════════════════════════════════════════════════════════

// CME delivery-month codes. F G H J K M N Q U V X Z is the standard set.
const MONTH_CODES = { F: 1, G: 2, H: 3, J: 4, K: 5, M: 6, N: 7, Q: 8, U: 9, V: 10, X: 11, Z: 12 };
const MONTH_NAMES = { jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9,
  oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12 };

// "Z26" -> { year: 2026, month: 12 }. Null for anything this cannot read —
// never a guessed month.
function contractDeliveryMonth(sym) {
  const s = String(sym || "").trim().toUpperCase();
  const m = /^([FGHJKMNQUVXZ])(\d{2})$/.exec(s);
  if (!m) return null;
  return { year: 2000 + Number(m[2]), month: MONTH_CODES[m[1]] };
}

// The last calendar month a shipment window covers, ONLY when the window states
// its own year. Returns null otherwise — see the header.
function windowLastMonth(window) {
  const toks = String(window || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  let year = null; const months = [];
  for (const tok of toks) {
    if (/^\d{4}$/.test(tok)) { year = Number(tok); continue; }
    if (/^\d{2}$/.test(tok)) { year = 2000 + Number(tok); continue; }
    if (MONTH_NAMES[tok] != null) months.push(MONTH_NAMES[tok]);
  }
  if (year == null) return null;                 // no year stated -> not asserted
  if (!months.length) return { year, month: 12 };// "2027 HARV" -> end of that year
  return { year, month: Math.max(...months) };
}

const firstOf = (ym) => `${ym.year}-${String(ym.month).padStart(2, "0")}-01`;
const monthAfter = (ym) => (ym.month === 12 ? { year: ym.year + 1, month: 1 } : { year: ym.year, month: ym.month + 1 });
const ymOf = (iso) => ({ year: Number(String(iso).slice(0, 4)), month: Number(String(iso).slice(5, 7)) });
const monthsBetween = (a, b) => (b.year - a.year) * 12 + (b.month - a.month);

// ── one target's lifecycle ────────────────────────────────────────────────
// `asOf` is an ISO date string and is REQUIRED — a lifecycle that reads the
// clock cannot be tested, and this one decides whether a row leaves the list.
function targetLifecycle(target, asOf) {
  const t = target || {};
  const reasons = [];
  const today = ymOf(asOf);
  const contract = contractDeliveryMonth(t.futuresContract ?? t.futures_contract);
  const win = windowLastMonth(t.deliveryWindow ?? t.delivery_window);

  if (!contract) reasons.push("contract_unreadable");

  // THE BOARD ROLL. The delivery month has begun, so the elevator is no longer
  // working orders on it.
  const boardRolled = contract ? monthsBetween(today, contract) <= 0 : false;
  if (boardRolled) reasons.push("board_in_delivery");

  // THE WINDOW, only where it states its year.
  let windowPassed = false;
  if (win) {
    windowPassed = monthsBetween(today, monthAfter(win)) <= 0;
    if (windowPassed) reasons.push("window_passed");
  } else if (t.deliveryWindow ?? t.delivery_window) {
    reasons.push("window_year_not_stated");
  }

  const expired = boardRolled || windowPassed;
  return {
    expired,
    // months until the board rolls; negative once it has. Null when the
    // contract cannot be read — never 0, which would sort as "rolls today".
    monthsToBoardRoll: contract ? monthsBetween(today, contract) : null,
    boardRollsOn: contract ? firstOf(contract) : null,
    windowEndsAfter: win ? firstOf(monthAfter(win)) : null,
    // "expiring soon" is a display nicety, not a rule: within one month of the
    // roll and not already gone.
    expiringSoon: !expired && contract ? monthsBetween(today, contract) <= 1 : false,
    reasons,
  };
}

// ── sorting and filtering, so the page is a tool and not a list ───────────
// Every column the page offers sorts through ONE comparator, because a table
// with per-column sort code drifts and starts disagreeing with its own header.
const SORT_KEYS = {
  farmer:      (r) => String(r.farmerName || "").toLowerCase(),
  entered:     (r) => String(r.enteredOn || ""),
  commodity:   (r) => String(r.commodity || "").toLowerCase(),
  point:       (r) => String(r.deliveryPoint || "").toLowerCase(),
  window:      (r) => String(r.deliveryWindow || "").toLowerCase(),
  // Board month sorts CHRONOLOGICALLY, not alphabetically — "H27" before "Z26"
  // as a string is a year apart the wrong way.
  board:       (r) => { const c = contractDeliveryMonth(r.futuresContract); return c ? c.year * 12 + c.month : 1e9; },
  bushels:     (r) => Number(r.bushels) || 0,
  cash:        (r) => Number(r.cashTarget) || 0,
  resting:     (r) => Number(r.futuresTargetEntered) || 0,
  needs:       (r) => (r.requiredFutures == null ? -1 : Number(r.requiredFutures)),
  // Today's board basis for this cell, and the basis the ticket was written
  // against. The gap between them IS the move, so both are sortable.
  basisNow:    (r) => (r.currentBasis == null ? -99 : Number(r.currentBasis)),
  basisEntry:  (r) => (r.basisAtEntry == null ? -99 : Number(r.basisAtEntry)),
  adjustment:  (r) => (r.adjustment == null ? 0 : Number(r.adjustment)),
  atStake:     (r) => Math.abs(Number(r.dollarsAtStake) || 0),
  status:      (r) => String(r.lane || ""),
};

function sortTargets(rows, key, dir = "asc") {
  const get = SORT_KEYS[key] || SORT_KEYS.atStake;
  const sign = dir === "desc" ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const x = get(a), y = get(b);
    if (x < y) return -1 * sign;
    if (x > y) return 1 * sign;
    // Stable tie-break so a re-sort never reshuffles equal rows under the user.
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
}

// A filter that matches NOTHING returns nothing — it never falls back to the
// whole list, which would read as "no filter applied" and hide the mistake.
function filterTargets(rows, f = {}) {
  const q = String(f.q || "").trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (f.lane && r.lane !== f.lane) return false;
    // "Needs changing" is TWO lanes — raise and lower — so it is its own
    // predicate rather than a lane value nothing sets.
    if (f.needsChange === true && !(r.lane === "burn" || r.lane === "miss")) return false;
    if (f.commodity && String(r.commodity || "").toLowerCase() !== String(f.commodity).toLowerCase()) return false;
    if (f.deliveryPoint && String(r.deliveryPoint || "") !== f.deliveryPoint) return false;
    if (f.futuresContract && String(r.futuresContract || "").toUpperCase() !== String(f.futuresContract).toUpperCase()) return false;
    if (f.farmerId && r.farmerId !== f.farmerId) return false;
    if (f.expired === true && !r.expired) return false;
    if (f.expired === false && r.expired) return false;
    if (f.enteredFrom && String(r.enteredOn || "") < f.enteredFrom) return false;
    if (f.enteredTo && String(r.enteredOn || "") > f.enteredTo) return false;
    if (f.cashMin != null && !(Number(r.cashTarget) >= f.cashMin)) return false;
    if (f.cashMax != null && !(Number(r.cashTarget) <= f.cashMax)) return false;
    if (q) {
      const hay = [r.farmerName, r.commodity, r.deliveryPoint, r.deliveryWindow,
                   r.futuresContract, r.buyer, r.notes].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

// The distinct values actually present, for populating the filter controls off
// the DATA rather than a hardcoded list — the convention the Coverage Book Scan
// option list follows, so a new delivery point appears on its own.
function filterFacets(rows) {
  const uniq = (fn) => [...new Set((rows || []).map(fn).filter(Boolean))].sort();
  return {
    commodities: uniq((r) => r.commodity),
    deliveryPoints: uniq((r) => r.deliveryPoint),
    contracts: [...new Set((rows || []).map((r) => r.futuresContract).filter(Boolean))]
      .sort((a, b) => (SORT_KEYS.board({ futuresContract: a }) - SORT_KEYS.board({ futuresContract: b }))),
    lanes: uniq((r) => r.lane),
  };
}

module.exports = {
  MONTH_CODES,
  contractDeliveryMonth,
  windowLastMonth,
  targetLifecycle,
  SORT_KEYS,
  sortTargets,
  filterTargets,
  filterFacets,
};

// ═══════════════════════════════════════════════════════════════════════════
// DID IT FILL? WE CANNOT KNOW — WE CAN ONLY SEE THE SETTLE.
//
// Kevin, 2026-08-24: "We cant be positive that they filled, but if the board
// closed above the strike price, we should move them to filled page."
//
// That is the honest claim and the wording matters. Our futures feed carries a
// SETTLE per contract per day — no high, no low, no tick — which is the exact
// reason grain target alerts have been paused since 2026-08-12. A settle-only
// read is wrong in BOTH directions: a rally through the level that settles back
// leaves no trace (we under-report), and a settle at the level says the board
// closed there, not that a resting order was hit (we over-state certainty).
//
// So this returns EVIDENCE — the date and the close that crossed — and the page
// calls it "board closed through", never "filled". Status is changed by a human.
// ═══════════════════════════════════════════════════════════════════════════
// grainContractTech comes from the same module as the board symbol because the
// gap scale MUST be the engine's own absMove — a second median-absolute-move
// here would be the second copy of a statistic whose definition the engine
// explicitly warns about.
const { grainBoardSymForMonth, grainContractTech } = require("../grainTargets");

// "corn" + "Z26" -> "CZ26", through the SAME rule the marketing plan uses to
// name a board. A second prefix map here is a rule defined twice.
const GRAIN_FOR_COMMODITY = { corn: "corn", wheat: "wheat", soybeans: "soybeans", plenish: "soybeans" };
function boardSymbolFor(commodity, futuresContract) {
  const c = contractDeliveryMonth(futuresContract);
  const g = GRAIN_FOR_COMMODITY[String(commodity || "").trim().toLowerCase()];
  if (!c || !g) return null;
  return grainBoardSymForMonth(g, c.year, c.month);
}

// The date from which the order has been resting AT ITS CURRENT LEVEL. A
// re-anchor moves the level, so crossings before it belong to the old one; a
// dismissal means the human already looked at everything up to that moment.
function restingSince(target) {
  const dates = [
    target.enteredOn ?? target.entered_on,
    (target.updatedAt ?? target.updated_at) ? String(target.updatedAt ?? target.updated_at).slice(0, 10) : null,
    (target.fillDismissedAt ?? target.fill_dismissed_at)
      ? String(target.fillDismissedAt ?? target.fill_dismissed_at).slice(0, 10) : null,
  ].filter(Boolean).map(String);
  return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : null;
}

// ⚠ THE LEVEL AN ORDER RESTS AT, PARSED ONCE — AND `Number(null)` IS `0`.
//
// Found by check-target-approach on its first run, 2026-10-06. Both this test
// and targetApproach read the level with a bare
// `Number(a ?? b)`, and `Number(null)` and `Number("")` are both ZERO, which is
// finite — so a `Number.isFinite` guard passes a missing price straight through
// as a $0.00 order. Measured: boardClosedThrough then returned
//
//   crossed: true, claim: "CZ26 settled 5.46 on 2026-09-01, at or above 0.00"
//
// which takes a priceless row OUT of the working book and files it under "the
// board settled through this" — the silent shortening of the list Kevin works
// from that targetLifecycle's own comment warns about, arriving by a new route.
//
// LATENT, NOT LIVE: 0 of the 16 rows in grain_cash_targets carry a null or zero
// futures target today, because the column is COMPUTED (`cash - basis`) rather
// than typed and the table CHECKs that the ticket balances. Latent is not fixed.
//
// This is the same shape CLAUDE.md already records twice — the SCO/ECO crop-year
// guard that let a null through onto the 86% lattice, and the LRP weight band
// where `Number(null)` produced a 0-0 cwt class that excluded every animal.
//
// ONE parser, both callers, so the two cannot disagree about whether a row has
// a price. A level of zero or below is not a price a sell order can rest at.
function targetLevel(target) {
  const raw = target && (target.futuresTargetEntered ?? target.futures_target_entered);
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// bars: [{ bar_date, close }] for THIS contract only. Narrowed by the caller,
// because two delivery years trade at once and a max across them is not a
// price this order could have been hit at.
function boardClosedThrough(target, bars, opts = {}) {
  const level = targetLevel(target);
  const sym = boardSymbolFor(target.commodity, target.futuresContract ?? target.futures_contract);
  const since = opts.since || restingSince(target);
  if (level == null || !sym || !since) {
    return { crossed: false, symbol: sym, since,
      reason: level == null ? "no futures target" : !sym ? "no board symbol" : "no resting date" };
  }
  let best = null;
  for (const b of bars || []) {
    const d = String(b.bar_date || "");
    const c = Number(b.close);
    if (!d || !Number.isFinite(c) || d < since) continue;
    // A SELL rests above the market and is hit on the way UP, so the settle
    // must reach it. `>=` because a settle exactly at the level did trade there.
    if (c >= level && (best == null || c > best.close)) best = { close: c, onDate: d };
  }
  return best
    ? { crossed: true, symbol: sym, since, level, close: best.close, onDate: best.onDate,
        // Never "filled". The page prints this.
        claim: `${sym} settled ${best.close.toFixed(4).replace(/0+$/, "").replace(/\.$/, ".00")} on ${best.onDate}, at or above ${level.toFixed(2)}` }
    : { crossed: false, symbol: sym, since, level };
}

// ── HOW CLOSE THE BOARD HAS COME TO THIS RESTING ORDER ─────────────────
//
// Kevin, 2026-10-06: "can cash targets be improved by implementing real
// technicals? There are several places within Silas that show targets, I think
// they are technically weak." He is right about the lane: the target LADDER in
// src/grainTargets.js computes moving averages, a Wilder RSI, quantiles, a close
// histogram and a percentile — and the cash-target ORDER BOOK, the entry form,
// the printed sheet, the alert and cashTargetBasis.js carry NO technical read at
// all. The book's columns are cash target / basis now / futures target / move,
// every one of them basis mechanics.
//
// ⚠ THE OBVIOUS READ IS WRONG, AND MEASURING IT FIRST IS WHAT CAUGHT THAT.
// The intuitive statistic for a resting sell order is where its price sits in
// the contract's own traded range. Measured over the live book AS OF EACH
// ORDER'S OWN ENTRY DATE — the only non-hindsight view — that read condemns
// correct practice: TEN of the fourteen filled targets were written ABOVE every
// close their contract had ever made, and all ten filled. Corcoran's CK27 was
// 45c above it, McIlroy's CN27 39c, Edmund's WN27 32c. Resting above the market
// is what a sell target IS. A percentile of one is ~100th by construction and
// distinguishes nothing — the same artifact CLAUDE.md already records for
// futures_contract_history's life-to-date percentile, where every live contract
// reads 100th because only the live ones are loaded in full. Shipping that flag
// would have marked ten of twelve successful tickets as problem orders.
//
// WHAT DOES SEPARATE THE BOOK is how close the board actually came while the
// order rested, and nothing computed it. boardClosedThrough answers the binary
// — did it cross — and discards the near miss. Measured 2026-10-06 over the two
// working orders, which the page renders identically:
//
//   LEFEBER       CZ26 $5.50   closest  4c short (2026-09-01), now  53c away
//   Cripple Creek WN27 $8.40   closest 76c short (2026-09-08), now 127c away
//
// Those are not the same situation. One was all but done and the market walked
// away from it; the other was never near. It also surfaces, for free and with
// no new alarm, the one filled row the board never settled through: Edmund Acres
// WN27 $7.80, closest 16c short — a fill that may well be real (an intraday
// trade, or a cash sale at the elevator) but has no board settlement behind it,
// which is boardClosedThrough's own "never 'filled'" doctrine run the other way.
//
// NO VERDICT AND NO RECOMMENDATION. It reports what the board did. It does not
// say whether a target is well placed, and it never says where to sell.
//
// PURE, like the rest of this file: no fetch, no React, no bare new Date().

// A gap expressed in the contract's OWN typical daily move, so that 53c on corn
// and 53c on soybeans are comparable figures.
//
// ⚠ `absMove` IS A FRACTION OF PRICE, not a price — see grainContractTech,
// which divides each daily change by that day's close precisely so it is
// comparable with the fill curve. The unit is therefore `absMove * last`. Using
// absMove raw is a ~5,000x error that renders every gap as "0x the daily move",
// which reads as a target sitting right on the market. Same shape as the
// mm_net_pct_oi fraction-vs-percent bug CLAUDE.md records, and as the
// stdev-vs-median-abs error the engine's own comment warns about one line above
// the statistic it defines.
//
// It is derived from the FULL bar set, not the resting window: the typical daily
// move is a property of the contract, not of how long this order has sat.
function approachMoveUnit(bars) {
  const tech = grainContractTech(bars || []);
  if (!tech || !Number.isFinite(tech.absMove) || !Number.isFinite(tech.last)) return null;
  const u = tech.absMove * tech.last;
  return Number.isFinite(u) && u > 0 ? u : null;
}

// bars: [{ bar_date, close }] for THIS contract only — narrowed by the caller,
// for the same reason boardClosedThrough says so: two delivery years trade at
// once and a max across them is not a price this order could have been hit at.
function targetApproach(target, bars, opts = {}) {
  const level = targetLevel(target);
  const sym = boardSymbolFor(target.commodity, target.futuresContract ?? target.futures_contract);
  // ⚠ THE SAME WINDOW boardClosedThrough USES, through the same rule. Two
  // windows on one row is a contradiction on screen: this would report "came
  // within 4c" for a settle the crossing test deliberately excluded as
  // belonging to a level the order no longer rests at.
  const since = opts.since || restingSince(target);
  if (level == null) return { ok: false, reason: "no futures target", symbol: sym, since };
  if (!sym) return { ok: false, reason: "no board symbol", since, level };
  if (!since) return { ok: false, reason: "no resting date", symbol: sym, level };

  const win = (bars || [])
    .filter((b) => b && b.bar_date && Number.isFinite(Number(b.close))
                   && String(b.bar_date) >= String(since))
    .sort((a, b) => (String(a.bar_date) < String(b.bar_date) ? -1 : 1));
  if (!win.length) return { ok: false, reason: "no settles since " + since, symbol: sym, since, level };

  let best = null;
  for (const b of win) {
    const c = Number(b.close);
    if (best == null || c > best.close) best = { close: c, onDate: String(b.bar_date) };
  }
  const lastBar = win[win.length - 1];
  const lastClose = Number(lastBar.close);
  const lastDate = String(lastBar.bar_date);
  // `>=` matches boardClosedThrough exactly: a settle AT the level did trade
  // there. The two must agree or a row can be "crossed" in one place and "1c
  // short" in the other.
  const reached = best.close >= level;
  const gapNow = level - lastClose;
  const unit = approachMoveUnit(bars);
  return {
    ok: true, symbol: sym, since, level, n: win.length,
    lastClose, lastDate,
    best,
    reached,
    // How far short the closest settle fell. Zero once reached, so a caller
    // cannot print "0c short" as if the board had merely grazed it.
    shortBy: reached ? 0 : level - best.close,
    gapNow,
    // The board is AT or THROUGH the order as of the latest settle. Not "0c
    // away" — a state of its own, and on a working row the most urgent one in
    // the book. It implies reached, so the two can never disagree.
    aboveMarket: lastClose >= level,
    // Cents the board has receded from its own closest approach.
    //
    // ⚠ NOT a "the gap widened" flag. shortBy is by construction the minimum
    // over the window, so any such flag is true for every order whose best day
    // is not today — the tautology the DRP signal's "Rolling over" column
    // already got caught on, where it printed for every poor entry. This is a
    // measured distance and it is exactly 0 when today IS the best day.
    offBest: reached ? 0 : Math.max(0, gapNow - (level - best.close)),
    bestIsNow: best.onDate === lastDate,
    moveUnit: unit,
    // A SCALE CONVERSION AND NEVER A FORECAST: "this gap is N times what this
    // contract typically moves in a day". It says nothing about how many days
    // it would take, or whether it will happen at all.
    gapMoves: unit ? gapNow / unit : null,
  };
}

module.exports.boardSymbolFor = boardSymbolFor;
module.exports.restingSince = restingSince;
module.exports.boardClosedThrough = boardClosedThrough;
module.exports.targetLevel = targetLevel;
module.exports.approachMoveUnit = approachMoveUnit;
module.exports.targetApproach = targetApproach;
